import { Control } from 'react-hook-form';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useRoleOptions, usePropertiesDropdownQuery } from '@/hooks/queries/useDropdowns';
import { Building2 } from 'lucide-react';
import type { DropdownProperty } from '@/types/api';
import type { EditUserFormData } from '../../../../../schema/user';

interface RolePermissionsSectionProps {
  control: Control<EditUserFormData>;
  properties: DropdownProperty[];
}

export default function RolePermissionsSection({
  control,
}: RolePermissionsSectionProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  
  // Fetch roles and properties dynamically from API
  const { options: roleOptions, isLoading: rolesLoading } = useRoleOptions();
  const { data: propertiesData = [], isLoading: propertiesLoading } = usePropertiesDropdownQuery();

  return (
    <div className={cn('space-y-4', isRTL && 'rtl')}>
      <div className={cn('space-y-2', isRTL && 'text-right')}>
        <h3 className="text-lg font-semibold">
          {t('users.edit.rolePermissions.title')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('users.edit.rolePermissions.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={cn(isRTL && 'text-right')}>
                {t('users.edit.rolePermissions.role')} *
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className={cn(isRTL && 'text-right')}>
                    <SelectValue
                      placeholder={t(
                        'users.edit.rolePermissions.placeholders.selectRole'
                      )}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {rolesLoading ? (
                    <SelectItem value="loading" disabled>
                      {t('common.loading', 'Loading...')}
                    </SelectItem>
                  ) : (
                    roleOptions.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={cn(isRTL && 'text-right')}>
                {t('users.edit.rolePermissions.status')} *
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className={cn(isRTL && 'text-right')}>
                    <SelectValue
                      placeholder={t(
                        'users.edit.rolePermissions.placeholders.selectStatus'
                      )}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {(['Active', 'Inactive'] as const).map(status => (
                    <SelectItem key={status} value={status}>
                      {t(
                        `users.edit.rolePermissions.statuses.${status.toLowerCase()}`
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Property Assignment - Only show for manager role */}
      {control._formValues.role?.toLowerCase() === 'manager' && (
        <div className="mt-4 space-y-4 p-4 bg-muted/30 rounded-lg border">
          <h3
            className={cn(
              'font-medium text-sm',
              isRTL ? 'text-right' : 'text-left'
            )}
          >
            {t('users.create.propertyAssignment.title')}
          </h3>

          <FormField
            control={control}
            name="property_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={cn(isRTL ? 'text-right' : 'text-left')}>
                  {t('users.create.propertyAssignment.assignedProperty')}
                </FormLabel>
                <Select
                  onValueChange={value =>
                    field.onChange(value === 'all' ? undefined : Number(value))
                  }
                  value={field.value?.toString() || 'all'}
                >
                  <FormControl>
                    <SelectTrigger
                      className={cn(isRTL ? 'text-right' : 'text-left')}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    >
                      <SelectValue
                        placeholder={t(
                          'users.create.placeholders.selectProperty'
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {propertiesLoading ? (
                      <SelectItem value="loading" disabled>
                        {t('users.create.loading')}
                      </SelectItem>
                    ) : (
                      <>
                        <SelectItem value="all">
                          <div
                            className={cn(
                              'flex items-center gap-2',
                              isRTL ? 'flex-row-reverse' : 'flex-row'
                            )}
                          >
                            <Building2 className="w-4 h-4" />
                            {t('users.create.propertyAssignment.allProperties')}
                          </div>
                        </SelectItem>
                        {propertiesData.map(property => (
                          <SelectItem
                            key={property.id}
                            value={property.id.toString()}
                          >
                            <div
                              className={cn(
                                'flex items-center gap-2',
                                isRTL ? 'flex-row-reverse' : 'flex-row'
                              )}
                            >
                              <Building2 className="w-4 h-4" />
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {property.label}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  Property ID: {property.id}
                                </span>
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                <FormMessage className={cn(isRTL ? 'text-right' : 'text-left')} />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
}
